export type AuthUiMessage = {
  title: string;
  description: string;
};

function pickBodyMessage(body: any): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  return (
    body.detail ||
    body.error_description ||
    body.error ||
    body.message
  );
}

function isNetworkyMessage(msg: string): boolean {
  const m = msg.toLowerCase();
  return (
    m.includes('failed to fetch') ||
    m.includes('networkerror') ||
    m.includes('err_connection_refused') ||
    m.includes('load failed')
  );
}

/** Detect AbortError / timeout errors (from bffClient withTimeout). */
function isTimeoutAbort(err: unknown): boolean {
  const name = (err as any)?.name;
  return name === 'AbortError' || name === 'TimeoutError';
}

/** Check if the error looks like an upstream/infrastructure issue rather than bad credentials. */
function isUpstreamInfraError(err: any): boolean {
  const body = err?.body;
  if (!body || typeof body !== 'object') return false;
  const code = body.error || '';
  return (
    code === 'upstream_error' ||
    code === 'upstream_not_found' ||
    code === 'upstream_shape' ||
    code === 'login_failed'
  );
}

export function authUiMessageFromError(
  err: unknown,
  opts?: { action?: 'login' | 'generic' }
): AuthUiMessage {
  const anyErr: any = err as any;
  const status: number | undefined = typeof anyErr?.status === 'number' ? anyErr.status : undefined;
  const bodyMsg = pickBodyMessage(anyErr?.body);
  const rawMsg: string | undefined = typeof anyErr?.message === 'string' ? anyErr.message : undefined;

  // ── Login-specific messages ──────────────────────────────────────
  if (opts?.action === 'login') {
    // Infrastructure / backend down — don't blame the user's credentials.
    if (isUpstreamInfraError(anyErr)) {
      return {
        title: 'Service unavailable',
        description: 'The authentication service is not responding. Please try again later or contact your administrator.',
      };
    }

    if (status === 401) {
      return {
        title: 'Invalid credentials',
        description: 'The username or password you entered is incorrect. Please check and try again.',
      };
    }

    if (status === 403) {
      return {
        title: 'Account restricted',
        description: 'Your account is deactivated or restricted. Contact your administrator for access.',
      };
    }

    if (status === 429) {
      return {
        title: 'Too many attempts',
        description: 'You have tried too many times. Please wait a moment before trying again.',
      };
    }
  }

  // ── General (non-login) auth messages ──────────────────────────────
  if (status === 401) {
    return {
      title: 'Session expired',
      description: 'Your session has ended. Please sign in again to continue.',
    };
  }

  if (status === 403) {
    return {
      title: 'Permission denied',
      description: 'You do not have access to this resource. Your role may have changed — try signing in again.',
    };
  }

  if (status === 502 || status === 503 || status === 504) {
    return {
      title: 'Server unavailable',
      description: "The server isn't responding right now. Please try again in a few moments.",
    };
  }

  if (isTimeoutAbort(err)) {
    return {
      title: 'Request timed out',
      description: "The server is taking too long to respond. It might be starting — please try again in a moment.",
    };
  }

  if (rawMsg && isNetworkyMessage(rawMsg)) {
    return {
      title: 'Connection error',
      description: "Can't reach the server. Check your network connection and try again.",
    };
  }

  // ── Fallback ───────────────────────────────────────────────────────
  // Try to extract something meaningful; avoid showing raw JSON/technical strings.
  const fallback = bodyMsg || rawMsg || '';
  const isTechnical = /upstream|jwt|token|signature|callUpstream|ECONNREFUSED/i.test(fallback);
  if (isTechnical || !fallback) {
    return {
      title: 'Something went wrong',
      description: 'An unexpected error occurred. Please try again or contact your administrator.',
    };
  }

  return {
    title: 'Sign-in failed',
    description: String(fallback),
  };
}
